import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

const MENU_WIDTH = 176;
const VIEWPORT_MARGIN = 8;

type ActionMenuProps = {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  align?: "left" | "right";
};

/**
 * Renders a floating menu into a portal on <body>, positioned via the anchor button's
 * bounding rect. This keeps the menu visible even when its row/card sits inside a
 * container with `overflow-hidden` (e.g. a rounded table wrapper) — a plain
 * `absolute` menu would get clipped by that ancestor instead of floating above it.
 */
export function ActionMenu({ open, onClose, anchorRef, children, align = "right" }: ActionMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return;
    }
    const reposition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = align === "right" ? rect.right - MENU_WIDTH : rect.left;
      const clampedLeft = Math.min(
        Math.max(VIEWPORT_MARGIN, left),
        window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN,
      );
      let top = rect.bottom + 6;
      const menuHeight = menuRef.current?.offsetHeight ?? 160;
      if (top + menuHeight > window.innerHeight - VIEWPORT_MARGIN) {
        top = Math.max(VIEWPORT_MARGIN, rect.top - menuHeight - 6);
      }
      setPos({ top, left: clampedLeft });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, anchorRef, align]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_WIDTH }}
      className="bg-card border border-border rounded-lg shadow-lg z-[200] overflow-hidden animate-in fade-in zoom-in-95 duration-100"
    >
      {children}
    </div>,
    document.body,
  );
}

export function ActionMenuItem({
  onClick,
  danger,
  variant = "default",
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  variant?: "default" | "destructive" | string;
  children: ReactNode;
}) {
  const isDestructive = variant === "destructive" || Boolean(danger);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3.5 py-2.5 text-sm hover:bg-muted transition ${isDestructive ? "text-destructive" : ""}`}
    >
      {children}
    </button>
  );
}
